import { useRef, type MouseEvent, type PointerEvent } from 'react'

/**
 * Closes a modal when its backdrop is clicked — and only then.
 *
 * The obvious version of this is one `onClick` handler that checks
 * `event.target === event.currentTarget`. It has a nasty flaw: a `click` fires
 * where the button is *released*, not where it was pressed. So selecting text
 * inside the dialog and letting go a few pixels outside it counts as a click on
 * the backdrop, and the dialog vanishes along with whatever was being edited.
 *
 * Remembering where the press started fixes it: the dialog closes only when the
 * pointer both went down and came up on the backdrop itself. A drag that begins
 * inside is a text selection, not a dismissal.
 *
 * Spread the returned props onto the backdrop element:
 *
 *     <div className="fixed inset-0 …" {...useBackdropClose(onClose)}>
 */
export function useBackdropClose(onClose: () => void) {
  const pressedOnBackdrop = useRef(false)

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      pressedOnBackdrop.current = event.target === event.currentTarget
    },
    onClick: (event: MouseEvent<HTMLElement>) => {
      if (pressedOnBackdrop.current && event.target === event.currentTarget) {
        onClose()
      }
      pressedOnBackdrop.current = false
    },
  }
}
